import DateUtils from "./date";
import { ESSPRESSIVE_INTEGRATION } from "../constants/originalState";
import { renderToStaticMarkup } from "react-dom/server";

const getLocaleFromCurrency = (currency: string) => {
  const upperCasedCurrency = currency.toUpperCase();
  const defaultLocale = 'en-US';
  const mapObject: { [key: string]: string } = {
    USD: 'en-US',
  };
  return mapObject[upperCasedCurrency] || defaultLocale;
};

export const convertNumberToCurrency = (
  value: number,
  currency: string = 'USD'
) => {
  const locale = getLocaleFromCurrency(currency);
  const numberFormat = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });
  return numberFormat.format(value);
};

export const stringToColor = (name: string) => {
  if (!name) return 'transparent';
  let hash = 0;
  let i;

  for (i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

export const stringAvatar = (name: string, size: number, customColor?: string) => {
  const arrStringName = !!name ? name.split(" ") : "ER";
  const isEsspressiveIntegration = name?.replace(/ /g,'')?.toUpperCase() === ESSPRESSIVE_INTEGRATION;
  const countLength = arrStringName.length;
  let initials = "";
  for (const word of arrStringName) {
    if (word !== "") {
      initials += word[0];
    } 
  }
  return {
    sx: {
      bgcolor: isEsspressiveIntegration ? (customColor ?? "#67ae3f") : stringToColor(!!name ? name : ""),
      width: size,
      height: size,
    },
    alt: name,
    title: name,
    children: `${(countLength > 1 ? initials : arrStringName[0]).slice(0, 2)}`,
  };
};

export function getAvatarName(name?: string) {
  const name_arr = !!name ? name.split(' ') : '';
  const avatar_name =
    name_arr?.length > 1
      ? `${name_arr[0][0]?.toUpperCase() || ''}${name_arr[1][0]?.toUpperCase() || ''}`
      : name_arr[0]?.slice(0, 1)?.toUpperCase();
  return avatar_name;
};

export const convertToShortNumber = (num: number, options?: { toUpperCase?: boolean, spacing?: boolean }) => {
  const { toUpperCase = true, spacing } = options || {};

  if (Math.abs(num) < 1000) {
    return num;
  }

  var shortNumber: string;
  var exponent: number;
  var size: number;
  var sign = num < 0 ? '-' : '';
  var suffixes: {
    [key: string]: number;
  } = {
    k: 6,
    m: 9,
    b: 12,
    t: 16,
  };

  num = Math.abs(num);
  size = Math.floor(num).toString().length;

  exponent = size % 3 === 0 ? size - 3 : size - (size % 3);
  shortNumber = (
    Math.round(10 * (num / Math.pow(10, exponent))) / 10
  ).toString();

  if (spacing) {
    shortNumber += ' ';
  }

  for (var suffix in suffixes) {
    if (exponent < suffixes[suffix]) {
      shortNumber += toUpperCase ? suffix.toUpperCase() : suffix;
      break;
    }
  }

  return (sign + shortNumber).trim();
};

export const sanitizeInput = (e: any, isSpecialCharacterAllowed: boolean) => {
  let regex = /[!"`'#%&:;<>={}~\$\(\)\*\+\\\?\[\]\^\|]/g;
  if (!isSpecialCharacterAllowed) {
    e.target.value = e.target.value.replace(regex, '');
    return e.target.value;
  }
};

export const getKbDate = (kbDetail: any, isPublishedDate: boolean, dateFormat?: string, timeZone?: string) => {
  let date = isPublishedDate
    ? kbDetail?.publishedDate
    : kbDetail?.lastUpdated || kbDetail?.publishedDate;

  if (dateFormat && timeZone) {
    date = DateUtils.convertToGMTWithFormat(date, dateFormat, timeZone);
  }

  const processedDate = DateUtils.timetoXbyGMT(date);

  return processedDate;
};

export function handleKeyPress(
  event: KeyboardEvent | React.KeyboardEvent<HTMLButtonElement>,
  key: string,
  func: any,
  thisArgs: any = null,
  ...args: any[]
) {
  if (event.key === key) {
    func.call(thisArgs, args);
  }
}

export const checkValidInputEmail = (emailValue: string): boolean => {
  if (emailValue?.trim().length > 0) {
    let checkValidEmail = String(emailValue)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    if (checkValidEmail !== null) return true;
  }
  return false;
};

interface IconOptions {
  size?: number;
  color?: string;
};

export const generateImgUrl = (imgUrl: string) => {
  const isLocal = window.location.hostname === 'localhost';
  return isLocal ? `https://uptime-dev.azurefd.net${imgUrl}` : imgUrl;
};

export const generateDataUrlFromSvgIcon = (muiIcon: React.ReactElement) => {
  const iconSvg = renderToStaticMarkup(muiIcon);
  const svgIconWithXmlns = iconSvg.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ');
  const dataUrl = `url('data:image/svg+xml,${encodeURIComponent(svgIconWithXmlns)}')`;
  return dataUrl;
};

export const createPseudoStyleFromMuiIcon = (muiIcon: React.ReactElement | null, options: IconOptions = {}) => {
  if (!muiIcon) return '';

  const { size = 24, color = 'currentColor' } = options;
  const dataUrl = generateDataUrlFromSvgIcon(muiIcon);

  return `
    width: ${size}px;
    height: ${size}px;
    display: inline-block;
    content: "";
    -webkit-mask: ${dataUrl} no-repeat 50% 50%;
    mask: ${dataUrl} no-repeat 50% 50%;
    -webkit-mask-size: cover;
    mask-size: cover;
    background-color: ${color};
  `;
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
